import {
  Box,
  Button,
  Card,
  Container,
  Group,
  List,
  SimpleGrid,
  Stack,
  Text,
  ThemeIcon,
  Title,
} from '@mantine/core';
import {
  IconArrowRight,
  IconCheck,
  IconHandStop,
  IconHeart,
  IconScale,
  IconShield,
} from '@tabler/icons-react';
import { Link } from 'react-router';

const principles = [
  {
    icon: IconHeart,
    title: 'Compassion First',
    description:
      'Every staff member is selected for their genuine empathy and dedication to resident well-being.',
  },
  {
    icon: IconScale,
    title: 'Ethical Standards',
    description:
      'We uphold the highest ethical standards in hiring, training, and day-to-day care practices.',
  },
  {
    icon: IconShield,
    title: 'Resident Safety',
    description:
      'Thorough background checks, ongoing training, and strict protocols protect our residents.',
  },
  {
    icon: IconHandStop,
    title: 'Zero Tolerance',
    description:
      'We maintain a zero-tolerance policy for neglect, abuse, or any compromise in care quality.',
  },
];

const team = [
  { role: 'Assisted Living Director' },
  { role: 'Director of Nursing (DNP)' },
  { role: 'Licensed Psychologist' },
  { role: 'Certified Food Production Manager' },
  { role: 'Master of Management Information Systems (MMIS) Technologist' },
  { role: 'Activities Director' },
  { role: 'Cultural Liaison' },
  { role: 'Pharmacist' },
];

const commitments = [
  'Licensed and credentialed experts across many care disciplines',
  'Ongoing continuing education and professional development',
  'Comprehensive background screening for every team member',
  'Staff-to-resident ratios that exceed state requirements',
  'Cultural sensitivity training for serving diverse communities',
  'Regular performance reviews focused on resident outcomes',
];

export function StaffingPage() {
  return (
    <>
      {/* Hero Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="lg" maw={800} mx="auto">
            <Text c="harmony.6" fw={600} tt="uppercase" size="sm">
              Our Staffing Philosophy
            </Text>
            <Title ta="center">Compassionate, Ethical Staffing</Title>
            <Text c="dimmed" size="lg" ta="center">
              It takes a village to provide truly exceptional care. At Hillside
              Harmony, our staffing practices are built on a foundation of
              compassion, integrity, and accountability — because every member
              of our team plays a vital role in the well-being of our residents.
            </Text>
          </Stack>
        </Container>
      </Box>

      {/* Principles Section */}
      <Box py={80}>
        <Container size="xl">
          <Stack align="center" gap="sm" mb={50}>
            <Title order={2} ta="center">
              Our Guiding Principles
            </Title>
            <Text c="dimmed" ta="center" maw={600}>
              These principles shape how we hire, train, and support every
              member of our care team.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 1, sm: 2, md: 4 }} spacing="xl">
            {principles.map((principle) => (
              <Card key={principle.title} padding="xl" ta="center">
                <ThemeIcon
                  size={60}
                  radius="xl"
                  variant="light"
                  color="harmony"
                  mx="auto"
                  mb="md"
                >
                  <principle.icon size={30} stroke={1.5} />
                </ThemeIcon>
                <Text fw={600} size="lg" mb="xs">
                  {principle.title}
                </Text>
                <Text c="dimmed" size="sm">
                  {principle.description}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Team Section */}
      <Box py={80} bg="gray.0">
        <Container size="xl">
          <Stack align="center" gap="sm" mb={50}>
            <Title order={2} ta="center">
              Our Team Of Experts For Modern Assisted Living
            </Title>
            <Text c="dimmed" ta="center" maw={600}>
              Our dedicated team brings together decades of experience in healthcare,
              hospitality, and senior services.
            </Text>
          </Stack>

          <SimpleGrid cols={{ base: 2, md: 4 }} spacing="xl">
            {team.map((member) => (
              <Card key={member.role} padding="lg" ta="center" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Text fw={600} size="lg">
                  {member.role}
                </Text>
              </Card>
            ))}
          </SimpleGrid>
        </Container>
      </Box>

      {/* Commitments Section */}
      <Box py={80} bg="gray.0">
        <Container size="md">
          <Stack align="center" gap="sm" mb={50}>
            <Title order={2} ta="center">
              Our Commitments
            </Title>
            <Text c="dimmed" ta="center" maw={600}>
              We hold ourselves to the highest standards so families can trust
              that their loved ones are in good hands.
            </Text>
          </Stack>

          <List
            spacing="md"
            size="lg"
            icon={
              <ThemeIcon color="harmony" size={28} radius="xl">
                <IconCheck size={18} stroke={2.5} />
              </ThemeIcon>
            }
          >
            {commitments.map((item) => (
              <List.Item key={item}>{item}</List.Item>
            ))}
          </List>
        </Container>
      </Box>

      {/* CTA Section */}
      <Box py={80}>
        <Container size="md">
          <Card padding="xl" radius="lg" bg="harmony.0">
            <Stack align="center" gap="lg">
              <Title order={2} ta="center">
                Meet Our Team In Person
              </Title>
              <Text c="dimmed" ta="center" maw={500}>
                Schedule a visit to see our compassionate staff in action and
                learn how we provide personalized care for every resident.
              </Text>
              <Group gap="md">
                <Button
                  component={Link}
                  to="/contact"
                  size="lg"
                  rightSection={<IconArrowRight size={18} />}
                >
                  Schedule a Tour
                </Button>
                <Button
                  component="a"
                  href="tel:+15551234567"
                  size="lg"
                  variant="outline"
                >
                  Call Us
                </Button>
              </Group>
            </Stack>
          </Card>
        </Container>
      </Box>
    </>
  );
}
